import React, { Fragment } from 'react';
import { syncSizeChartTexts, loadSizeChartTexts, saveSizeChartTexts, updateSizeChartTexts, resetSizeChartErrors, SIZE_CHART_SLUG } from '../../modules/sizeChart'
import { activatePartnerIntegration } from '../../modules/appIntegrations';
import { updateAutoTranslations } from '../../modules/shop';
import API from '../../api/api';
import { wantsMachineTranslation, isSkippedByDefault, skipReasonOf } from '../../machineTranslationRules';
import MachineTranslationDialog, { excerptOf, ICON_KEY_CONTEXTS } from '../../components/MachineTranslationDialog';
import RechargePlans from '../../components/RechargePlans';
import withRouter from 'with-router'
import { Link as RouterLink } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SyncIcon from '@mui/icons-material/Sync';
import ItemsList from '../../components/ItemsList'
import ItemsListSkeleton from '../../components/ItemsListSkeleton'
import LoadingIndicator from '../../components/LoadingIndicator';
import EmptyView from '../../components/EmptyView';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CheckIcon from '@mui/icons-material/Check';
import Collapse from '@mui/material/Collapse';
import Checkbox from '@mui/material/Checkbox';
import CloseIcon from '@mui/icons-material/Close';
import { withTranslation } from 'react-i18next';

import { _getAttributeProgress } from '../../helper';
import { glintOnHover } from '../../theme/glint';

// Translation page for the MP Size Chart app (Shopplaza/Avada).

const isChartMetafield = (key) => /^content_configs_\d+$/.test(key || '');
// items split off a metafield carry a suffix: <metafield id>-chart-<chart index> for the charts,
// <metafield id>-recommend for the size recommendation quiz. Ids end up in DOM ids and the rich
// text editors' #id selectors, so only CSS-safe characters (no dots, no colons). The metafield
// id is what the save endpoint keys on (kept numeric, as the backend delivers it)
const chartItemId = (metafieldId, index) => `${metafieldId}-chart-${index}`;
const recommendItemId = (metafieldId) => `${metafieldId}-recommend`;
const metafieldIdOf = (itemId) => {
  const raw = String(itemId).split('-')[0];
  return /^\d+$/.test(raw) ? Number(raw) : raw;
};
// the quiz is one feature with ~30 strings and no on/off flag in the metafield: its own item
// keeps the general settings readable for merchants who never use it
const RECOMMEND_PREFIX = 'settings.recommend.';
const isRecommendField = (field) => field.path.startsWith(RECOMMEND_PREFIX);
// update loop statuses that the retry button picks up again
const FAILED_STATUS = ['failed', 'translateFailed', 'saveFailed'];
// The backend delivers per metafield a flat, typed, labeled field list - no
// walking of the raw metafield JSON happens here.
// the list type decides the section's conventions, among them whether the translation dialog's
// field table writes keys out or shows them behind an icon (ICON_KEY_CONTEXTS)
const LIST_TYPE = 'apps';

class SizeChartApp extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      itemsLanguage: null,
      itemsGeneration: 0,
      listGeneration: 0,
      errorLockCollapse: false,
      hasUntranslatable: false,
      // the notice was closed without action for this visit; the title bar button stays
      updateDialogDismissed: false,
      // the title bar button re-syncs first so the loop works on a fresh dry run
      updateChecking: false,
      // the running/finished loop: {languages: [{id, code, name}], status: {id: <status key>}, finished,
      // translate, plan: {id: {fields, byMetafield, words}}, translated: {id: {key: string}}}
      updateRun: null,
      // "update and translate": the word count per language before anything is spent
      // {counting, languages, perLanguage: {id: {fields, byMetafield, words}}, totalWords, error}
      translatePlan: null,
      // the button found nothing to do
      updateUpToDate: false,
      // the title bar check opened the notice: then languages without any translation are offered
      // too; the automatic notice on page open stays reserved for real changes
      noticeRequested: false,
      // the merchant's ticks in the details table, by language id; a language without a tick of
      // its own is in when it holds translations and out when it holds none yet
      noticeTicks: {},
      updateDetailsOpen: false,
      updateHoverRow: null,
      showRechargePlans: false,
      planError: false,
    }
  }

  componentDidMount() {
    this.props.activatePartnerIntegration(SIZE_CHART_SLUG);
    this.props.syncSizeChartTexts(this.props.editLanguage);
  }

  componentDidUpdate(prevProps) {
    if (this.props.editLanguage !== prevProps.editLanguage) {
      this.props.loadSizeChartTexts(this.props.editLanguage);
    }
  }

  componentWillReceiveProps(newProps) {
    if (
      (this.props.sizeChart.sync_size_chart_pending === true && newProps.sizeChart.sync_size_chart_pending === false) ||
      (this.props.sizeChart.load_size_chart_pending === true && newProps.sizeChart.load_size_chart_pending === false) ||
      (this.props.sizeChart.save_size_chart_pending === true && newProps.sizeChart.save_size_chart_pending === false)
    ) {
      const toField = field => ({
        // dots in the JSON path break the rich text editors' #id selectors, so the
        // DOM-safe id and the real path are carried separately
        id: field.path.replace(/\./g, '-'),
        path: field.path,
        type: field.type,
        label: field.label,
        source: field.source,
        target: field.target === null ? '' : field.target,
        // the source changed since this translation was written (backend snapshot)
        outdated: field.outdated === true,
        // false: numbers and size tokens - not sent to the machine translation, not counted in
        // the progress, shown greyed as a placeholder in the empty target
        machineTranslate: field.machineTranslate !== false,
        sourcePlaceholder: field.machineTranslate === false,
      });
      // sizes and numbers read the same in every language: they neither count towards the
      // progress nor hold it back
      const progressOf = (fields) => {
        const translatable = fields.filter(f => f.machineTranslate !== false);
        return translatable.length > 0 ? _getAttributeProgress(translatable, 'target') : 100;
      };
      const toItem = (id, title, fields) => ({
        type: 'appEmbed',
        id,
        progress: progressOf(fields),
        synchronized: true,
        title,
        info: '',
        fields: fields.map(toField),
      });

      const items = [];
      (newProps.sizeChart.metafields || []).forEach(metafield => {
        if(!isChartMetafield(metafield.key)) {
          const recommend = metafield.fields.filter(isRecommendField);
          items.push(toItem(metafield.id, metafield.label, metafield.fields.filter(f => !isRecommendField(f))));
          if(recommend.length > 0) {
            // titled like the charts by the merchant's own words: the widget headline from the
            // app, with the backend's leading crumb as fallback. The crumb leaves the field labels
            const crumb = recommend[0].label.split(' > ')[0];
            const headline = recommend.find(f => f.path === RECOMMEND_PREFIX + 'title')?.source;
            const fields = recommend.map(f => ({ ...f, label: f.label.startsWith(crumb + ' > ') ? f.label.slice(crumb.length + 3) : f.label }));
            items.push(toItem(recommendItemId(metafield.id), typeof headline === 'string' && headline.trim() !== '' ? headline.trim() : crumb, fields));
          }
          return;
        }
        // a chart metafield holds an array of charts (Shopplaza splits into more metafields only
        // at Shopify's size limit), every path starts with the chart's index: one item per chart,
        // stitched back together per metafield in onSave
        const byChart = new Map();
        metafield.fields.forEach(field => {
          const index = field.path.split('.')[0];
          if(!byChart.has(index)) byChart.set(index, []);
          byChart.get(index).push(field);
        });
        byChart.forEach((fields, index) => {
          const title = (metafield.chartNames || {})[index] || this.props.t('sizeChart.unnamedChart', { index: Number(index) + 1 });
          items.push(toItem(chartItemId(metafield.id, index), title, fields));
        });
      });

      const hasUntranslatable = items.some(item => item.fields.some(f => f.sourcePlaceholder));
      // stamped with the context they were built for, see currentItems()
      this.setState({ items, hasUntranslatable, itemsLanguage: newProps.editLanguage, itemsGeneration: this.state.listGeneration });
    }
  }

  onCollapsed = (id) => {}

  // the backend expects the complete field list per metafield (it merges the flat fields back
  // into one full JSON blob and copies the source over every path it does not receive). The
  // list passes only the edited items, so the untouched sibling charts of a metafield come
  // from state - otherwise saving one chart would wipe the others' translations
  // confirmedFieldIds: fields a machine translation run just wrote or a "keep translation" named;
  // selectedFields: the list's pending edits, keyed `${itemId}-${fieldId}`. Both go out as
  // confirmed: "this target is current for the live source" - the backend refreshes the field's
  // recorded source even when the text is unchanged, which clears its outdated flag
  onSave = (items, selectedFields, language, confirmedFieldIds = []) => {
    const confirmed = new Set((confirmedFieldIds || []).map(String));
    const isConfirmed = (item, field) => confirmed.has(String(field.id)) || Object.prototype.hasOwnProperty.call(selectedFields || {}, `${item.id}-${field.id}`);
    const edited = new Map(items.map(item => [String(item.id), item]));
    const touched = new Set(items.map(item => metafieldIdOf(item.id)));
    const perMetafield = new Map();
    this.state.items.forEach(stored => {
      const metafieldId = metafieldIdOf(stored.id);
      if(!touched.has(metafieldId)) return;
      const item = edited.get(String(stored.id)) || stored;
      if(!perMetafield.has(metafieldId)) perMetafield.set(metafieldId, []);
      perMetafield.get(metafieldId).push(...item.fields.map(field => ({
        path: field.path,
        // a target equal to the source is a translation like any other (per-leaf copy backend)
        target: field.target || '',
        confirmed: isConfirmed(item, field),
      })));
    });
    const data = Array.from(perMetafield, ([id, fields]) => ({ id, fields }));

    this.props.saveSizeChartTexts(this.props.editLanguage, data, res => {});
  }

  // the notice's one action: the update, with the machine translation of the new and changed
  // texts when there are any (the word count comes first, nothing is spent unseen)
  onUpdateFromNotice = () => {
    const languages = this.noticeSelection();
    if(languages.some(l => ((l.empty || 0) + (l.outdated || 0)) > 0)) {
      this.startTranslatePlan(languages);
    } else {
      this.startUpdateRun(languages);
    }
  }

  isNoticeLanguageTicked = (l) => this.state.noticeTicks[l.id] ?? !l.untranslated;
  noticeSelection = () => (this.props.sizeChart.pendingUpdate?.languages || []).filter(this.isNoticeLanguageTicked);
  toggleNoticeLanguage = (l) => {
    this.setState({ noticeTicks: { ...this.state.noticeTicks, [l.id]: !this.isNoticeLanguageTicked(l) } });
  }
  // the head tick: all in, or all out once every language is in
  toggleAllNoticeLanguages = (languages) => {
    const allTicked = languages.every(this.isNoticeLanguageTicked);
    const noticeTicks = {};
    languages.forEach(l => { noticeTicks[l.id] = !allTicked; });
    this.setState({ noticeTicks });
  }

  // title bar button: fresh dry run, then the notice - never the update itself without the
  // merchant seeing what it is about. Brings back a notice that was clicked away
  onUpdate = () => {
    this.setState({ updateChecking: true });
    this.props.syncSizeChartTexts(this.props.editLanguage, res => {
      const languages = (res?.pending_update || {}).languages || [];
      if(languages.length > 0) this.forgetNoticeDismissal();
      // fresh ticks for a fresh list; the table opens when it holds languages to opt into
      this.setState({ updateChecking: false, updateUpToDate: res?.success === true && languages.length === 0, updateDialogDismissed: false, noticeRequested: true, noticeTicks: {}, updateDetailsOpen: this.state.updateDetailsOpen || languages.some(l => l.untranslated) });
    });
  }

  // a clicked-away notice stays away for good, whatever changes in the app later: the title bar
  // button opens it on request
  noticeKey = () => 'ly_sizeChartUpdateDismissed';
  isNoticeDismissed = () => {
    try { return localStorage.getItem(this.noticeKey()) === '1'; } catch(e) { return false; }
  }
  forgetNoticeDismissal = () => {
    try { localStorage.removeItem(this.noticeKey()); } catch(e) { /* storage blocked: the notice just shows again */ }
  }
  onDismissNotice = () => {
    try { localStorage.setItem(this.noticeKey(), '1'); } catch(e) { /* see above */ }
    this.setState({ updateDialogDismissed: true, noticeRequested: false });
  }

  startUpdateRun = (languages, plan = null) => {
    const status = {};
    languages.forEach(l => { status[l.id] = 'waiting'; });
    this.setState({
      translatePlan: null,
      updateRun: { languages, status, finished: false, translate: !!plan, plan: plan || {}, translated: {} },
    }, this.runNextUpdate);
  }

  setRunStatus = (id, status, extra = {}, then) => {
    const current = this.state.updateRun;
    if(!current) return;
    this.setState({ updateRun: { ...current, ...extra, status: { ...current.status, [id]: status } } }, then);
  }

  // one request per language, strictly sequential: the dialog ticks the list along. With a
  // translate plan the language goes update -> translate -> save; the update alone is done
  // when the translate part fails, which the status says
  runNextUpdate = () => {
    const run = this.state.updateRun;
    if(!run) return;
    const next = run.languages.find(l => run.status[l.id] === 'waiting');
    if(!next) {
      this.finishUpdateRun();
      return;
    }
    this.setRunStatus(next.id, 'updating');
    this.props.updateSizeChartTexts(next.id, res => {
      if(!this.state.updateRun) return;
      if(res?.success !== true) {
        this.setRunStatus(next.id, 'failed', {}, this.runNextUpdate);
        return;
      }
      const plan = this.state.updateRun.plan[next.id];
      if(!this.state.updateRun.translate || !plan || plan.fields.length === 0) {
        this.setRunStatus(next.id, 'done', {}, this.runNextUpdate);
        return;
      }
      this.translateLanguage(next, plan);
    });
  }

  // the strings of a language as the translate route takes them: one flat map keyed per field
  fieldKey = (metafieldId, path) => `${metafieldId}::${path}`;

  translateLanguage = (language, plan) => {
    // a retry after a failed save must not spend the words a second time
    const already = this.state.updateRun.translated[language.id];
    if(already) {
      this.saveTranslatedLanguage(language, plan, already);
      return;
    }
    this.setRunStatus(language.id, 'translating');
    const map = {};
    plan.fields.forEach(f => { map[this.fieldKey(f.metafieldId, f.path)] = f.source; });
    API.autoTranslateStrings(this.fromLanguageCode(), language.code.toUpperCase(), map, res => {
      if(!this.state.updateRun) return;
      if(res?.auto_translations) {
        this.props.updateAutoTranslations(res.auto_translations.total_used_words, res.auto_translations.available_words);
      }
      if(res?.success === false || !res?.Strings) {
        this.setRunStatus(language.id, 'translateFailed', {}, this.runNextUpdate);
        return;
      }
      const translated = { ...this.state.updateRun.translated, [language.id]: res.Strings };
      this.setState({ updateRun: { ...this.state.updateRun, translated } }, () => this.saveTranslatedLanguage(language, plan, res.Strings));
    });
  }

  saveTranslatedLanguage = (language, plan, strings) => {
    this.setRunStatus(language.id, 'saving');
    // the save takes the complete field list per metafield. The planned fields go out as
    // confirmed: the run translated them for the live source, even where the result reads the
    // same as before (an outdated translation that came back identical), which clears outdated
    const planned = new Set(plan.fields.map(f => this.fieldKey(f.metafieldId, f.path)));
    const data = Object.entries(plan.byMetafield).map(([id, fields]) => ({
      id: /^\d+$/.test(id) ? Number(id) : id,
      fields: fields.map(f => {
        const key = this.fieldKey(id, f.path);
        const target = strings[key] !== undefined ? strings[key] : f.target;
        return { path: f.path, target: target || '', confirmed: planned.has(key) };
      }),
    }));
    API.savePartnerTexts(SIZE_CHART_SLUG, language.id, data, res => {
      if(!this.state.updateRun) return;
      this.setRunStatus(language.id, res?.success === true ? 'translated' : 'saveFailed', {}, this.runNextUpdate);
    });
  }

  fromLanguageCode = () => (this.props.languages.languages.find(x => x.base == true)?.code || '').toUpperCase();

  // "update app content" with texts to translate: the candidates of every affected language go
  // into the shared machine translation dialog, counts aggregated over the languages - nothing
  // is spent before the merchant has seen the total. The rows the section flags (numbers,
  // sizes) start unticked, like in the field list's dialog
  startTranslatePlan = (languages) => {
    this.setState({ translatePlan: { counting: true, recounting: false, languages, perLanguage: {}, summary: [], excluded: new Set(), totalWords: 0 } }, () => this.collectPlan(languages));
  }

  collectPlan = (languages) => {
    const perLanguage = {};
    const summary = [];
    const excluded = new Set();
    const loadNext = (index) => {
      if(!this.state.translatePlan) return;
      if(index >= languages.length) {
        this.setState({ translatePlan: { ...this.state.translatePlan, perLanguage, summary, excluded } }, this.countPlan);
        return;
      }
      const language = languages[index];
      API.getPartnerTexts(SIZE_CHART_SLUG, language.id, res => {
        if(!this.state.translatePlan) return;
        if(res?.success !== true) {
          this.failPlan();
          return;
        }
        const fields = [];
        const byMetafield = {};
        (res.data || []).forEach(metafield => {
          byMetafield[metafield.id] = metafield.fields.map(f => ({ path: f.path, source: f.source, target: f.target === null ? '' : f.target }));
          metafield.fields.forEach(f => {
            // the shared rule set: empty fields and outdated translations, never images
            if(!wantsMachineTranslation(f, 'outdated')) return;
            const id = `${language.id}::${this.fieldKey(metafield.id, f.path)}`;
            const filled = f.target !== '' && f.target !== null && f.target !== undefined;
            fields.push({ id, metafieldId: metafield.id, path: f.path, source: f.source });
            // one table for all languages: the key icon (hover: the field's label) plus the language
            summary.push({ id, fullKey: f.label || f.path, language: language.name, languageId: language.id, source: excerptOf(f.source), target: excerptOf(f.target), replaces: filled, suggestedSkip: isSkippedByDefault(f), skipReason: skipReasonOf(f) });
            if(isSkippedByDefault(f)) excluded.add(id);
          });
        });
        perLanguage[language.id] = { fields, byMetafield, words: 0 };
        loadNext(index + 1);
      });
    };
    loadNext(0);
  }

  // a language's ticked candidates
  plannedFields = (languageId) => {
    const plan = this.state.translatePlan;
    return ((plan.perLanguage[languageId] || {}).fields || []).filter(f => !plan.excluded.has(f.id));
  }

  // the word count of the ticked rows, one request per language (the count route takes one
  // target language); the dialog opens when the last one is in
  countPlan = () => {
    const plan = this.state.translatePlan;
    if(!plan) return;
    const from = this.fromLanguageCode();
    const words = {};
    // the service's skips (word count with skip_details): such rows are marked and unticked;
    // word_count never included them, so the numbers stand without a second count
    const serviceSkips = {};
    const countNext = (index) => {
      const current = this.state.translatePlan;
      if(!current) return;
      if(index >= plan.languages.length) {
        const perLanguage = {};
        Object.entries(current.perLanguage).forEach(([id, entry]) => { perLanguage[id] = { ...entry, words: words[id] || 0 }; });
        const totalWords = Object.values(words).reduce((n, w) => n + w, 0);
        const excluded = new Set(current.excluded);
        Object.keys(serviceSkips).forEach(id => excluded.add(id));
        const summary = current.summary.map(line => serviceSkips[line.id] === undefined ? line
          : { ...line, suggestedSkip: true, skipReason: line.skipReason || 'service', skipHint: serviceSkips[line.id] });
        this.setState({ translatePlan: { ...current, excluded, summary, counting: false, recounting: false, perLanguage, totalWords } });
        return;
      }
      const language = plan.languages[index];
      const fields = this.plannedFields(language.id);
      if(fields.length === 0) {
        words[language.id] = 0;
        countNext(index + 1);
        return;
      }
      const map = {};
      fields.forEach(f => { map[this.fieldKey(f.metafieldId, f.path)] = f.source; });
      API.autoTranslateWordCount(from, language.code.toUpperCase(), map, count => {
        if(!this.state.translatePlan) return;
        if(!count || count.success === false) {
          this.failPlan();
          return;
        }
        words[language.id] = count.word_count ?? 0;
        (count.skip_details?.fields || []).forEach(entry => { if(entry?.key !== undefined) serviceSkips[`${language.id}::${entry.key}`] = entry.reason || ''; });
        countNext(index + 1);
      });
    };
    countNext(0);
  }

  // a tick change in the table: the remaining rows are counted again (free) once the clicking
  // has paused
  onTogglePlanField = (id) => {
    const plan = this.state.translatePlan;
    if(!plan) return;
    const excluded = new Set(plan.excluded);
    if(excluded.has(id)) excluded.delete(id);
    else excluded.add(id);
    clearTimeout(this.recountTimer);
    this.setState({ translatePlan: { ...plan, excluded, recounting: true } }, () => {
      this.recountTimer = setTimeout(this.countPlan, 400);
    });
  }

  failPlan = () => {
    this.setState({ translatePlan: null, planError: true });
  }

  onStartTranslatePlan = () => {
    const plan = this.state.translatePlan;
    const perLanguage = {};
    plan.languages.forEach(l => {
      const entry = plan.perLanguage[l.id];
      if(entry) perLanguage[l.id] = { ...entry, fields: this.plannedFields(l.id) };
    });
    this.startUpdateRun(plan.languages, perLanguage);
  }

  onCancelTranslatePlan = () => {
    clearTimeout(this.recountTimer);
    this.setState({ translatePlan: null });
  }

  // cancel in the translation dialog ends the whole flow for this visit: the notice would otherwise
  // come straight back for a second click. The title bar button brings it back on request; the
  // "don't show again" flag stays as it is
  onCloseTranslateDialog = () => {
    clearTimeout(this.recountTimer);
    this.setState({ translatePlan: null, updateDialogDismissed: true, noticeRequested: false });
  }

  onGetMoreWords = () => {
    this.setState({ translatePlan: null, showRechargePlans: true });
  }

  finishUpdateRun = () => {
    const run = this.state.updateRun;
    const failed = Object.values(run.status).some(status => FAILED_STATUS.includes(status));
    // a new list generation: the update re-merged the translations, the list must not carry
    // its current targets over into the reloaded data. A clean run ends without a word; only
    // failures stay on screen with a retry
    // the languages left unticked keep their pending state, but the merchant chose that: the
    // notice stays away for this visit and the title bar check brings it back
    this.setState({
      updateRun: failed ? { ...run, finished: true } : null,
      listGeneration: this.state.listGeneration + 1,
      updateDialogDismissed: true,
      noticeRequested: false,
    });
    this.props.loadSizeChartTexts(this.props.editLanguage);
  }

  // the loading overlay's line while the run works: which language (the step would only churn)
  runMessage = (run) => {
    const current = run.languages.find(l => ['updating', 'translating', 'saving'].includes(run.status[l.id]));
    return current ? this.props.t('sizeChart.updateDialog.running', { language: current.name }) : undefined;
  }

  onRetryFailedUpdates = () => {
    const run = this.state.updateRun;
    const status = { ...run.status };
    Object.keys(status).forEach(id => { if(FAILED_STATUS.includes(status[id])) status[id] = 'waiting'; });
    this.setState({ updateRun: { ...run, status, finished: false } }, this.runNextUpdate);
  }

  onDismissUpdateDialog = () => {
    this.setState({ updateRun: null, updateUpToDate: false, updateDialogDismissed: true });
  }

  // The list keeps the targets of items it already knows across a reload (it adopts new data
  // only on its own pending edges, and re-uses old targets for synchronized items). It is
  // therefore remounted per language and per update generation - and must not be seeded with
  // items of the previous context on that first render, which state alone cannot guarantee.
  currentItems = () => (
    this.state.itemsLanguage === this.props.editLanguage && this.state.itemsGeneration === this.state.listGeneration
      ? this.state.items
      : []
  )

  // the dry run per language, like the "show fields" table of the machine translation dialog:
  // untranslated texts, the ones added since the language was last saved, the translations whose
  // source changed (to retranslate) and whether a translated block sits somewhere else now
  renderUpdateDetails = (languages, languageLabel) => {
    const { t } = this.props;
    // secondary colour like the app's MUI table heads, bold so the columns read at a glance
    const head = { color: 'text.secondary', fontWeight: 700 };
    const cell = (i, extra = {}) => ({
      display: 'flex', alignItems: 'center', minWidth: 0, px: 0.75, py: 0.25,
      bgcolor: this.state.updateHoverRow === i ? 'action.hover' : 'transparent',
      cursor: i === null ? 'default' : 'pointer',
      ...extra,
    });
    const number = { justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' };
    return (
      <Box sx={{ mt: 1, maxHeight: 220, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr) max-content max-content max-content max-content', fontSize: 13 }}>
        <Box component="span" sx={{ ...cell(null, { px: 0 }), ...head }}>
          <Checkbox size="small" sx={{ p: 0.5 }}
            checked={languages.length > 0 && languages.every(this.isNoticeLanguageTicked)}
            indeterminate={languages.some(this.isNoticeLanguageTicked) && !languages.every(this.isNoticeLanguageTicked)}
            onChange={() => this.toggleAllNoticeLanguages(languages)}
            inputProps={{ 'aria-label': t('sizeChart.updateDialog.columnLanguage') }} />
        </Box>
        <Box component="span" sx={{ ...cell(null), ...head }}>{t('sizeChart.updateDialog.columnLanguage')}</Box>
        <Box component="span" sx={{ ...cell(null, number), ...head }}>{t('sizeChart.updateDialog.columnUntranslated')}</Box>
        <Box component="span" sx={{ ...cell(null, number), ...head }}>{t('sizeChart.updateDialog.columnAdded')}</Box>
        <Box component="span" sx={{ ...cell(null, number), ...head }}>{t('sizeChart.updateDialog.columnRetranslate')}</Box>
        <Box component="span" sx={{ ...cell(null, { justifyContent: 'center' }), ...head }}>{t('sizeChart.updateDialog.columnStructure')}</Box>
        {languages.map((l, i) => {
          // the whole row toggles the tick; the checkbox itself stops the click so it toggles once
          const rowHandlers = {
            onMouseEnter: () => this.setState({ updateHoverRow: i }),
            onMouseLeave: () => this.setState({ updateHoverRow: null }),
            onClick: () => this.toggleNoticeLanguage(l),
          };
          const added = l.added || 0;
          // a non-zero count is what the reader looks for; zeros step back
          const count = (n) => <Box component="span" sx={{ fontWeight: n > 0 ? 'bold' : 'normal', color: n > 0 ? 'text.primary' : 'text.secondary' }}>{n}</Box>;
          return (
            <React.Fragment key={l.id}>
              {/* the run takes the ticked languages only: a merchant can leave a language out */}
              <Box component="span" {...rowHandlers} sx={cell(i, { borderRadius: '4px 0 0 4px', px: 0 })}>
                <Checkbox size="small" sx={{ p: 0.5 }} checked={this.isNoticeLanguageTicked(l)} onChange={() => this.toggleNoticeLanguage(l)} onClick={e => e.stopPropagation()} inputProps={{ 'aria-label': languageLabel(l) }} />
              </Box>
              <Box component="span" {...rowHandlers} sx={cell(i, { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>{languageLabel(l)}</Box>
              {/* untranslated and new are disjoint: the new texts are untranslated by definition */}
              <Box component="span" {...rowHandlers} sx={cell(i, number)}>{count(Math.max((l.empty || 0) - added, 0))}</Box>
              <Box component="span" {...rowHandlers} sx={cell(i, number)}>{count(added)}</Box>
              <Box component="span" {...rowHandlers} sx={cell(i, number)}>{count(l.outdated || 0)}</Box>
              <Box component="span" {...rowHandlers} sx={cell(i, { justifyContent: 'center', borderRadius: '0 4px 4px 0', color: 'text.secondary' })}>
                {l.moved ? <CheckIcon sx={{ fontSize: 16 }} /> : '\u2013'}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  render() {
    const { t } = this.props;

    const partner = (this.props.appIntegrations.partners || []).find(x => x.slug === SIZE_CHART_SLUG);
    const appTitle = partner?.title;
    const unavailable = this.props.sizeChart.unavailable;

    const run = this.state.updateRun;
    const runActive = !!run && !run.finished;
    const plan = this.state.translatePlan;
    const isPending =
      this.props.sizeChart.sync_size_chart_pending ||
      this.props.sizeChart.load_size_chart_pending ||
      this.props.sizeChart.save_size_chart_pending ||
      this.props.sizeChart.update_size_chart_pending ||
      // no saves in the gaps between the loop's requests either
      runActive || this.state.updateChecking || !!plan;

    // the sync's dry run found stored translations that no longer match the charts' structure
    const pendingUpdate = this.props.sizeChart.pendingUpdate;
    // the notice stays up while the word count runs, its button turns into the progress
    const counting = !!plan && plan.counting;
    // isPending counts the plan itself, which would close the notice the moment the count starts
    const busyOtherwise = isPending && !counting;
    const noticeLanguages = pendingUpdate?.languages || [];
    // on its own the notice opens for changed translations only; asked for, it also lists the
    // languages that have none yet
    const hasNoticeContent = (pendingUpdate?.count || 0) > 0 || (this.state.noticeRequested && noticeLanguages.length > 0);
    const onlyUntranslated = (pendingUpdate?.count || 0) === 0;
    const showNotice = !run && (!plan || counting) && !this.state.updateUpToDate && !this.state.updateDialogDismissed && !busyOtherwise && hasNoticeContent && !this.isNoticeDismissed();
    const availableWords = this.props.shop.auto_translations?.available_words ?? 0;
    // moved blocks only, nothing empty and nothing outdated: the update is the whole story, the
    // notice says so and skips the word count
    const noticeSelection = this.noticeSelection();
    const hasEmptyFields = noticeSelection.some(l => ((l.empty || 0) + (l.outdated || 0)) > 0);
    // the shared dialog's numbers, aggregated over the languages; no "translate what fits" part
    // across languages, the merchant unticks rows or buys words
    const planData = plan && !plan.counting ? (() => {
      const included = plan.summary.filter(line => !plan.excluded.has(line.id));
      return {
        fieldsNum: included.length,
        fieldSummary: plan.summary,
        wordsNum: plan.totalWords,
        availableWords,
        missingNum: Math.max(plan.totalWords - availableWords, 0),
        partFieldsNum: 0,
        overwriteNum: included.filter(line => line.replaces).length,
        hasMuchHtml: false,
      };
    })() : null;
    // Shopify's locale names are unique per shop, the code would only repeat what the name says
    const languageLabel = (l) => l.name;
    const hasFailed = !!run && Object.values(run.status).some(s => FAILED_STATUS.includes(s));

    return <React.Fragment>
      <Typography variant="h4" gutterBottom component="h2">
        {t('navigation.primary.apps')}
        {appTitle &&
          <Fragment>
            <ChevronRightIcon />
            {appTitle}
          </Fragment>
        }
        {!unavailable &&
        <Button
          variant="outlined"
          size="small"
          // sits on the purple title bar: the theme's blue outline is barely visible there
          sx={{ float: 'right', color: '#fff', borderColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.08)' }, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.5)', borderColor: 'rgba(255, 255, 255, 0.3)' } }}
          startIcon={<SyncIcon />}
          disabled={isPending}
          onClick={this.onUpdate}
        >
          {t('sizeChart.checkChangesBtn')}
        </Button>
        }
      </Typography>

      <Divider className="divider-white" />

      {this.state.hasUntranslatable && !unavailable &&
        <Alert severity="info">
          {t('sizeChart.untranslatableNotice')}
        </Alert>
      }

      <ItemsList
        key={`size-chart-${this.props.editLanguage}-${this.state.listGeneration}`}
        virtualized={true}
        type={LIST_TYPE}
        extraRouteSegment={SIZE_CHART_SLUG}
        pagination={false}
        paginate={false}
        hideSorting={true}
        sortFields={true}
        // the filter bar narrows the fields inside a chart, and labels such as
        // "Table block 2 > Column 3 heading" are worth matching
        allowFieldFiltering={true}
        searchFieldLabels={true}
        onCollapsed={this.onCollapsed}
        isPending={isPending}
        isSaving={
          this.props.sizeChart.save_size_chart_pending
        }
        onSave={this.onSave}
        data={this.currentItems()}
        allowAutoTranslation={true}
        flagsOutdated={true}
        error={this.props.sizeChart.error}
        errorLockCollapse={this.state.errorLockCollapse}
        saveErrors={this.props.sizeChart.saveErrors}
        emptyMessage={
          unavailable === 'free_plan' ?
          <EmptyView
            title={t('sizeChart.unavailable.freePlanTitle')}
            description={t('sizeChart.unavailable.freePlanDescription')}
            bottom={
              <Button variant="contained" color="primary" component={RouterLink} to="/profile/plans" sx={glintOnHover}>
                {t('plans.planSelect.upgradePlan')}
              </Button>
            }
          />
        : unavailable === 'no_metafields' ?
          // the app may be missing or simply hold no chart yet; the listing covers the first case,
          // the text the second
          <EmptyView
            title={t('sizeChart.unavailable.noChartsTitle')}
            description={t('sizeChart.unavailable.noChartsDescription')}
            bottom={partner?.storeLink &&
              <Button variant="contained" color="primary" href={partner.storeLink} target="_blank" rel="noopener noreferrer">
                {t('partnerIntegrations.openListingTitle')}
              </Button>
            }
          />
        : (isPending === false && this.currentItems().length === 0) ?
          <EmptyView
            title={'No translation fields available'}
          />
        :
          <ItemsListSkeleton rows={5} />
        }
      />
      {/* closable without action (x, backdrop, escape): the merchant may just want to leave */}
      {showNotice &&
        <Dialog open onClose={counting ? this.onCancelTranslatePlan : this.onDismissNotice} maxWidth="sm">
          <DialogTitle sx={{ pr: 6 }}>
            {t(onlyUntranslated ? 'sizeChart.updateDialog.untranslatedTitle' : 'sizeChart.updateDialog.title')}
            {/* closing during the count cancels it and keeps the notice's dismissal untouched */}
            <IconButton aria-label={t('system.close')} onClick={counting ? this.onCancelTranslatePlan : this.onDismissNotice} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              {t(onlyUntranslated ? 'sizeChart.updateDialog.untranslatedDescription' : 'sizeChart.updateDialog.description')}
            </DialogContentText>
            <DialogContentText sx={{ mb: 1 }}>
              {t(onlyUntranslated ? 'sizeChart.updateDialog.actionTranslateUntranslated' : hasEmptyFields ? 'sizeChart.updateDialog.actionTranslate' : 'sizeChart.updateDialog.actionUpdateOnly', { button: t('partnerIntegrations.updateContentBtn') })}
            </DialogContentText>
            {/* the languages live in the details table: usually all of them are affected. The
                toggle sits on a divider that separates the text from the table */}
            <Divider textAlign="right" sx={{ mt: 1 }}>
              <Button size="small" variant="text" sx={{ p: 0, minWidth: 0, textTransform: 'none' }} onClick={() => this.setState({ updateDetailsOpen: !this.state.updateDetailsOpen })}>
                {t(this.state.updateDetailsOpen ? 'sizeChart.updateDialog.hideDetails' : 'sizeChart.updateDialog.showDetails')}
              </Button>
            </Divider>
            <Collapse in={this.state.updateDetailsOpen}>
              {this.renderUpdateDetails(pendingUpdate?.languages || [], languageLabel)}
            </Collapse>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.onUpdateFromNotice} color="primary" variant="contained" disabled={counting || noticeSelection.length === 0} startIcon={counting ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}>
              {t(counting ? 'sizeChart.updateDialog.counting' : 'partnerIntegrations.updateContentBtn')}
            </Button>
          </DialogActions>
        </Dialog>
      }
      {planData &&
        <MachineTranslationDialog
          data={planData}
          excluded={plan.excluded}
          iconKeys={ICON_KEY_CONTEXTS.includes(LIST_TYPE)}
          onToggleField={this.onTogglePlanField}
          recounting={!!plan.recounting}
          onClose={this.onCloseTranslateDialog}
          onConfirm={this.onStartTranslatePlan}
          onGetMoreWords={this.onGetMoreWords}
        />
      }
      <RechargePlans
        visible={this.state.showRechargePlans}
        onClose={() => this.setState({ showRechargePlans: false })}
        onBuy={(rechargePlan) => API.buyWords(rechargePlan, () => {})}
      />
      {this.state.planError &&
        <Dialog open onClose={() => this.setState({ planError: false })} maxWidth="xs">
          <DialogTitle>{t('partnerIntegrations.updateContentBtn')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('sizeChart.updateDialog.planError')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ planError: false })} color="primary" variant="contained">
              {t('system.close')}
            </Button>
          </DialogActions>
        </Dialog>
      }
      {run && run.finished && hasFailed &&
        <Dialog open onClose={this.onDismissUpdateDialog} maxWidth="xs" fullWidth>
          <DialogTitle>{t('partnerIntegrations.updateContentBtn')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>{t('sizeChart.updateDialog.failedIntro')}</DialogContentText>
            <List dense disablePadding>
              {run.languages.filter(l => FAILED_STATUS.includes(run.status[l.id])).map(l =>
                <ListItem key={l.id} disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}><ErrorOutlineIcon color="error" /></ListItemIcon>
                  <ListItemText primary={languageLabel(l)} secondary={t(`sizeChart.updateDialog.status.${run.status[l.id]}`)} />
                </ListItem>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.onRetryFailedUpdates} color="primary" variant="outlined" startIcon={<SyncIcon />}>
              {t('sizeChart.updateDialog.retryBtn')}
            </Button>
            <Button onClick={this.onDismissUpdateDialog} color="primary" variant="contained">
              {t('system.close')}
            </Button>
          </DialogActions>
        </Dialog>
      }
      {this.state.updateUpToDate &&
        <Dialog open onClose={this.onDismissUpdateDialog} maxWidth="xs">
          <DialogTitle>{t('partnerIntegrations.updateContentBtn')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('sizeChart.updateDialog.upToDate')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.onDismissUpdateDialog} color="primary" variant="contained">
              {t('system.close')}
            </Button>
          </DialogActions>
        </Dialog>
      }
      <LoadingIndicator
        // the word count dialog reports its own progress, no overlay behind it; the update run
        // is the plain overlay, its line names the language and step at hand
        pending={isPending && !plan}
        // the logo spinner throughout, saves included, like the main sections
        message={runActive ? this.runMessage(run) : undefined}
        loadingType={this.props.sizeChart.save_size_chart_pending ? 'saving' : 'loading'}
        error={this.props.sizeChart.error}
        onErrorCancel={this.props.resetSizeChartErrors}
        onErrorRetry={() => {
          this.setState({ errorLockCollapse: true }, () => {
            this.setState({ errorLockCollapse: false });
          });
        }}
      />
    </React.Fragment>
  }
}


const mapStateToProps = ({ sizeChart, appIntegrations, shop, languages }) => ({
  sizeChart: sizeChart,
  appIntegrations: appIntegrations,
  languages: languages,
  editLanguage: shop.shop.edit_language,
  shopUrl: shop.shop.url,
  shopPlan: shop.shop.charge.name,
  shop: shop.shop
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      syncSizeChartTexts,
      loadSizeChartTexts,
      saveSizeChartTexts,
      updateSizeChartTexts,
      resetSizeChartErrors,
      activatePartnerIntegration,
      updateAutoTranslations,
    },
    dispatch
  )

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SizeChartApp)))
